import React, { useEffect, useMemo, useState } from 'react';
import './YearRecap.css';

const formatDuration = (seconds) => {
  const total = Number.isFinite(seconds) ? seconds : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours === 0) {
    return `${minutes} м`;
  }
  return `${hours} ч ${minutes} м`;
};

const formatHours = (seconds) => {
  const hours = (Number.isFinite(seconds) ? seconds : 0) / 3600;
  return Math.round(hours * 10) / 10;
};

const formatDate = (isoDate) => {
  if (!isoDate) return '—';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString('ru-RU');
};

const YearRecap = ({ data, year, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animation, setAnimation] = useState('enter');
  const [shared, setShared] = useState(false);

  const totalWatchSeconds = Number.isFinite(data?.totalWatchTimeSec) ? data.totalWatchTimeSec : 0;
  const totalWatchHours = formatHours(totalWatchSeconds);
  const recapYear = year || data?.year || new Date().getFullYear();

  const slides = useMemo(() => {
    const topWatchList = (data?.topByWatchTime || []).slice(0, 3).map((item, index) => ({
      title: item.animeName,
      meta: `${item.episodes} серий`,
      sub: formatDuration(item.watchTimeSec),
      accent: `#${index + 1}`
    }));

    const topEpisodes = (data?.topByEpisodes || []).slice(0, 3).map((item, index) => ({
      title: item.animeName,
      meta: `${item.episodes} серий`,
      sub: `${formatDuration(item.watchTimeSec)} в эфире`,
      accent: `Top ${index + 1}`
    }));

    return [
      {
        id: 'cover',
        badge: 'Year Recap',
        title: `Итоги ${recapYear}`,
        subtitle: 'Показываем, во что превратился твой аниме-год',
        stats: [
          { label: 'Серий', value: data?.episodesFinished ?? '—' },
          { label: 'Уникальных тайтлов', value: data?.uniqueAnimeWatched ?? '—' },
          { label: 'Активных дней', value: data?.activeDays ?? '—' },
        ],
        note: `${totalWatchHours} ч в эфире`,
        background: 'linear-gradient(145deg, #0c111a, #141a24)',
        glass: true,
      },
      {
        id: 'time',
        badge: 'Драйв',
        title: 'Как ты смотрел',
        subtitle: 'Темп, марафоны и средняя длина сессии',
        stats: [
          { label: 'Часов', value: totalWatchHours },
          { label: 'Мин / активный день', value: Math.round(data?.averageWatchMinutesPerActiveDay ?? 0) },
          { label: 'Сессий', value: data?.sessions ?? '—' },
        ],
        note: `Средняя сессия — ${Math.round(data?.averageSessionMinutes ?? 0)} мин`,
        background: 'linear-gradient(145deg, #0f1624, #1a2230)',
      },
      {
        id: 'top-watch',
        badge: 'Залипал',
        title: 'Топ по времени',
        subtitle: 'Тайтлы, которые держали тебя дольше всего',
        list: topWatchList,
        background: 'linear-gradient(145deg, #0f1722, #121927)',
      },
      {
        id: 'top-episodes',
        badge: 'Марафон',
        title: 'Топ по сериям',
        subtitle: 'Где ты оставил больше всего прогресса',
        list: topEpisodes,
        background: 'linear-gradient(145deg, #0b1221, #172033)',
      },
      {
        id: 'moments',
        badge: 'События',
        title: 'Твои пики',
        subtitle: 'Сильные дни и первые/последние открытия',
        dual: [
          {
            heading: 'День марафона',
            title: formatDate(data?.bingeDay?.date),
            meta: `${formatDuration((data?.bingeDay?.watchTimeSec || 0))}`,
            foot: `${data?.bingeDay?.episodes || 0} серий`,
          },
          {
            heading: 'Первый старт',
            title: data?.firstWatch?.animeName || '—',
            meta: formatDate(data?.firstWatch?.timestamp),
            foot: `С ${data?.firstWatch?.series + 1 || 1} серии`,
          },
          {
            heading: 'Последний кадр года',
            title: data?.lastWatch?.animeName || '—',
            meta: formatDate(data?.lastWatch?.timestamp),
            foot: `С ${data?.lastWatch?.series + 1 || 1} серии`,
          },
        ],
        background: 'linear-gradient(145deg, #0d1826, #0f1f2e)',
        layout: 'triple',
      },
      {
        id: 'taste',
        badge: 'Вкус',
        title: 'Твои жанры и студии',
        subtitle: 'Настроение, которое выбираешь чаще всего',
        tags: (data?.topGenres || []).slice(0, 5),
        note: data?.favoriteStudio ? `Любимая студия — ${data.favoriteStudio}` : '',
        background: 'linear-gradient(145deg, #151b2a, #1f2a3c)',
      },
      {
        id: 'community',
        badge: 'Сообщество',
        title: 'Вклад и связи',
        subtitle: 'Друзья, отзывы и поддержка проекта',
        stats: [
          { label: 'Друзей', value: data?.communityStats?.friends ?? 0 },
          { label: 'Подписок на тайтлы', value: data?.communityStats?.followingAnime ?? 0 },
          { label: 'Рамок', value: data?.communityStats?.framesUnlocked ?? 0 },
          { label: 'Рецензий', value: data?.reviewStats?.written ?? 0 },
        ],
        note: data?.subscriptionStats?.active ? 'Спасибо за подписку — ты держишь сервис на плаву.' : 'Подписка неактивна, но ты всё равно часть комьюнити.',
        background: 'linear-gradient(145deg, #0f1724, #171f2f)',
        glass: true,
      },
      {
        id: 'share',
        badge: 'Готов к сторис',
        title: 'Поделись с друзьями',
        subtitle: 'Скопируем ключевые цифры в буфер обмена',
        background: 'linear-gradient(145deg, #0d1117, #13202f)',
        cta: 'Скопировать итоги',
        highlight: shared ? 'Скопировано!' : 'Пусть друзья знают, что ты смотрел',
      },
    ];
  }, [data, recapYear, shared, totalWatchHours]);

  useEffect(() => {
    setAnimation('enter');
    const timer = setTimeout(() => setAnimation(''), 400);
    return () => clearTimeout(timer);
  }, [currentSlide]);

  const handleNext = () => {
    if (currentSlide >= slides.length - 1) return;
    setAnimation('exit');
    setTimeout(() => {
      setCurrentSlide((prev) => prev + 1);
    }, 220);
  };

  const handlePrev = () => {
    if (currentSlide === 0) return;
    setAnimation('exit');
    setTimeout(() => {
      setCurrentSlide((prev) => prev - 1);
    }, 220);
  };

  const handleShare = async () => {
    const lines = [
      `Итоги ${recapYear} на animix`,
      `Серий: ${data?.episodesFinished ?? '—'}`,
      `Уникальных тайтлов: ${data?.uniqueAnimeWatched ?? '—'}`,
      `Всего часов: ${totalWatchHours}`,
      `Топ по времени: ${(data?.topByWatchTime?.[0]?.animeName) || '—'}`,
      `Любимый жанр: ${(data?.topGenres && data.topGenres[0]) || '—'}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setShared(true);
    } catch (err) {
      setShared(false);
    }
  };

  const slide = slides[currentSlide];

  return (
    <div className="year-recap-overlay">
      <div className="year-recap-frame">
        <div className="year-recap-progress">
          {slides.map((_, idx) => (
            <div
              key={slides[idx].id + idx}
              className={`year-recap-progress__step ${idx <= currentSlide ? 'active' : ''}`}
            />
          ))}
        </div>

        <button className="year-recap-close ui-close ui-close--xs" onClick={onClose} aria-label="Закрыть рекап">×</button>

        <div className={`year-recap-card ${animation}`} style={{ background: slide.background }}>
          <div className="yr-card-noise" />
          <div className="yr-card-glow" />

          <div className="year-recap-card__header">
            <span className="yr-pill">{slide.badge}</span>
            <div className="yr-step">{currentSlide + 1}/{slides.length}</div>
          </div>

          <div className="year-recap-card__body">
            <div className="yr-main">
              <h2>{slide.title}</h2>
              <p className="yr-sub">{slide.subtitle}</p>

              {slide.list && (
                <div className="yr-list">
                  {slide.list.map((item) => (
                    <div key={item.title} className="yr-list__item">
                      <div className="yr-list__accent">{item.accent}</div>
                      <div className="yr-list__meta">
                        <div className="yr-list__title">{item.title}</div>
                        <div className="yr-list__desc">{item.meta}</div>
                        <div className="yr-list__sub">{item.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {slide.dual && (
                <div className={`yr-dual ${slide.layout === 'triple' ? 'triple' : ''}`}>
                  {slide.dual.map((item) => (
                    <div key={item.heading + item.title} className="yr-dual__card">
                      <span className="yr-pill small">{item.heading}</span>
                      <div className="yr-dual__title">{item.title}</div>
                      <div className="yr-dual__meta">{item.meta}</div>
                      <div className="yr-dual__foot">{item.foot}</div>
                    </div>
                  ))}
                </div>
              )}

              {slide.tags && (
                <div className="yr-tags">
                  {slide.tags.map((tag) => (
                    <span key={tag} className="yr-tag">#{tag}</span>
                  ))}
                </div>
              )}

              {slide.stats && (
                <div className="yr-stats">
                  {slide.stats.map((stat) => (
                    <div key={stat.label} className="yr-stat">
                      <div className="yr-stat__value">{stat.value}</div>
                      <div className="yr-stat__label">{stat.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {slide.note && <div className="yr-note">{slide.note}</div>}
              {slide.highlight && <div className="yr-highlight">{slide.highlight}</div>}
            </div>
          </div>

          <div className="year-recap-card__footer">
            <div className="yr-actions">
              <button className="yr-btn ghost" onClick={handlePrev} disabled={currentSlide === 0}>
                ← Назад
              </button>
              <div className="yr-actions__right">
                {slide.cta ? (
                  <button className="yr-btn" onClick={handleShare}>
                    {shared ? 'Скопировано' : slide.cta}
                  </button>
                ) : null}
                <button
                  className="yr-btn primary"
                  onClick={handleNext}
                  disabled={currentSlide === slides.length - 1}
                >
                  Далее →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YearRecap;
